import { Ok, Result } from 'ts-res';
import { Cookie as CookieData } from 'src/kameleoonData/cookie';
import { KameleoonData } from 'src/kameleoonData/types';
import { exhaustCheck, KameleoonError } from 'src/kameleoonError';
import {
  EvaluationDataType,
  NameMatchType,
  ValueMatchType,
} from 'src/targeting/types';
import { CookieType, OptionalCookieType } from 'src/kameleoonData/types';
import { Parser } from 'src/utilities';
import { ConditionDataType, ICondition } from './types';
import { KameleoonLogger } from 'src/logging';

export class Cookie implements ICondition {
  private cookie: Partial<CookieType>;
  private keyMatchType?: NameMatchType | null;
  private valueMatchType?: ValueMatchType | null;

  constructor({
    name,
    value,
    nameMatchType,
    valueMatchType,
  }: ConditionDataType) {
    this.cookie = {
      key: name ?? undefined,
      value: value ?? undefined,
    };
    this.keyMatchType = nameMatchType;
    this.valueMatchType = valueMatchType;
  }

  public evaluate({
    targetingData,
  }: EvaluationDataType): Result<boolean, KameleoonError> {
    const isEmptyCookie = !this.cookie.key && !this.cookie.value;
    const isValidMatchType =
      Boolean(this.valueMatchType) && Boolean(this.keyMatchType);

    if (isEmptyCookie || !isValidMatchType) {
      return Ok(false);
    }

    const extractedCookie = this.extractCookie();
    const cookieData = targetingData?.[KameleoonData.Cookie]?.cookie || [];

    return Ok(
      extractedCookie.some((pair) => this.checkCondition(pair)) ||
        cookieData.some((pair) => this.checkCondition(pair)),
    );
  }

  private extractCookie(): CookieType[] {
    if (typeof document !== 'undefined' && document.cookie) {
      return CookieData.fromString(document.cookie).data.cookie;
    }

    return [];
  }

  private checkCondition(dataCookie: CookieType): boolean {
    const cookieMatchType = this.keyMatchType!;
    const { key, value } = dataCookie;

    if (cookieMatchType === NameMatchType.OPTIONAL) {
      const conditionCookie = this.cookie;

      return this.isOptionalCookie(conditionCookie)
        ? this.compareCookieValue(value, conditionCookie.value)
        : false;
    }

    const { key: compareKey, value: compareValue } = this.cookie as CookieType;

    let keyMatch: boolean;

    switch (cookieMatchType) {
      case NameMatchType.EXACT:
        keyMatch = key === compareKey;
        break;
      case NameMatchType.CONTAINS:
        keyMatch = key.includes(compareKey);
        break;
      case NameMatchType.REGULAR_EXPRESSION:
        keyMatch = Parser.parseRegExp(compareKey).test(key);
        break;
      default:
        KameleoonLogger.error`Unexpected comparing operation for Cookie condition: ${cookieMatchType}`;
        exhaustCheck(cookieMatchType);
    }

    return keyMatch && this.compareCookieValue(value, compareValue);
  }

  private compareCookieValue(value: string, compareValue: string): boolean {
    const valueMatchType = this.valueMatchType!;

    switch (valueMatchType) {
      case ValueMatchType.EXACT:
        return value === compareValue;
      case ValueMatchType.CONTAINS:
        return value.includes(compareValue);
      case ValueMatchType.REGULAR_EXPRESSION:
        return Parser.parseRegExp(compareValue).test(value);
      default:
        KameleoonLogger.error`Unexpected comparing operation for Cookie condition: ${valueMatchType}`;
        return false;
    }
  }

  private isOptionalCookie(
    cookie: Partial<CookieType>,
  ): cookie is OptionalCookieType {
    const { value } = cookie;

    return typeof value !== 'undefined';
  }
}
